import {
    productQuantity,
    UNIT_MEASURE_CODE,
    type ProductQuantityRules,
} from "@ecommerce/shared-utils/src/quantity/productQuantity";
import type { AddCartItemInput, CartItem } from "../types/cart.types";

export const CART_FALLBACK_MAX_QUANTITY = 999;

type CartQuantityCarrier = Pick<
    CartItem,
    "stock" | "unitMeasureCode" | "unitLabel" | "cantDecimal" | "minimumQuantity"
>;

export interface CartItemQuantityContext {
    unitMeasureCode: number;
    unitLabel: string;
    cantDecimal: number;
    minimumQuantity: number;
    stock: number | undefined;
    maximumQuantity: number;
    rules: ProductQuantityRules;
}

const isRepresentableMinimum = (
    value: number,
    cantDecimal: number,
    baseMinimum: number,
): boolean => {
    const validation = productQuantity.validate(value, {
        cantDecimal,
        storefrontMinimum: baseMinimum,
    });
    return validation.valid && value >= baseMinimum;
};

export const getCartItemQuantityContext = (item: CartQuantityCarrier): CartItemQuantityContext => {
    const unitMeasureCode =
        Number.isInteger(item.unitMeasureCode) && (item.unitMeasureCode ?? 0) > 0
            ? item.unitMeasureCode!
            : UNIT_MEASURE_CODE;
    const cantDecimal =
        unitMeasureCode === UNIT_MEASURE_CODE
            ? 0
            : productQuantity.resolvePrecision(item.cantDecimal);
    const baseMinimum = productQuantity.getMinimum({ cantDecimal });
    const minimumQuantity =
        item.minimumQuantity != null &&
        Number.isFinite(item.minimumQuantity) &&
        isRepresentableMinimum(item.minimumQuantity, cantDecimal, baseMinimum)
            ? item.minimumQuantity
            : baseMinimum;
    const stock =
        item.stock != null && Number.isFinite(item.stock)
            ? (productQuantity.getMaximumSelectableStock(item.stock, cantDecimal) ?? undefined)
            : undefined;
    const maximumQuantity = stock ?? CART_FALLBACK_MAX_QUANTITY;

    return {
        unitMeasureCode,
        unitLabel:
            item.unitLabel?.trim() ||
            (unitMeasureCode === UNIT_MEASURE_CODE ? "Unidades" : "Medida"),
        cantDecimal,
        minimumQuantity,
        stock,
        maximumQuantity,
        rules: {
            cantDecimal,
            storefrontMinimum: minimumQuantity,
            stock: maximumQuantity,
        },
    };
};

export const normalizeCartItemMetadata = <T extends CartItem | AddCartItemInput>(item: T): T => {
    const context = getCartItemQuantityContext(item);

    return {
        ...item,
        stock: context.stock,
        unitMeasureCode: context.unitMeasureCode,
        unitLabel: context.unitLabel,
        cantDecimal: context.cantDecimal,
        minimumQuantity: context.minimumQuantity,
    };
};

export const getValidCartQuantity = (
    value: number,
    context: CartItemQuantityContext,
): number | null => {
    const validation = productQuantity.validate(value, context.rules);
    if (!validation.valid || value < context.minimumQuantity) return null;

    return productQuantity.normalize(value, context.cantDecimal);
};

export const clampCartQuantity = (
    value: number,
    context: CartItemQuantityContext,
): number | null => {
    const validation = productQuantity.validate(value, {
        cantDecimal: context.cantDecimal,
    });
    if (
        !validation.valid &&
        validation.error !== "below-minimum" &&
        validation.error !== "above-stock"
    ) {
        return null;
    }

    const normalized = productQuantity.normalize(value, context.cantDecimal);
    return Math.min(Math.max(normalized, context.minimumQuantity), context.maximumQuantity);
};

export const formatCartQuantity = (item: CartQuantityCarrier, value: number): string =>
    productQuantity.format(value, getCartItemQuantityContext(item).cantDecimal);
